import classnames from "classnames";
import { isEmpty } from "lodash";
import React, { Fragment, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";

import { useDelayedAction } from "@krea/customer-features/hooks";
import FieldRequestExplanation from "@krea/customer-features/shared-components/field-request/FieldRequestExplanation";
import FieldRequestPreview from "@krea/customer-features/shared-components/field-request/FieldRequestPreview";
import { FieldRequestForm } from "@krea/customer-features/shared-components/field-request/GetFieldRequestForm";
import { Text } from "@krea/customer-features/shared-components/text";
import { useIsFieldRequestSubmitting } from "@krea/customer-features/store/field-requests/hooks/mutations";
import { useGetOneFieldRequest } from "@krea/customer-features/store/field-requests/hooks/queries";

const EditablePreviewFieldRequest = ({
  fieldRequestID,
  preview,
  withInputLabel = false,
  disableEdit = false,
  style = {},
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const isFieldRequestSubmitting = useIsFieldRequestSubmitting(fieldRequestID);
  const delayAction = useDelayedAction(isFieldRequestSubmitting);

  const { data: request = {} } = useGetOneFieldRequest(fieldRequestID);

  const changeIsOpen = () => {
    setIsOpen(!isOpen);
  };

  const ToggleFieldRequest = () => {
    delayAction(changeIsOpen);
  };

  const editButtonText = isOpen
    ? t("iaq.close")
    : request.isSubmitted
      ? t("iaq.edit")
      : t("iaq.add");

  if (isEmpty(request)) {
    return null;
  }

  const customLabel = () => {
    switch (request.field) {
      case "reason":
        return t("applicationForm.commons.reasonLabel");
      case "reasonExplanation":
        return t("iaq.reason.reasonExplanationTitle");
      case "property":
        return t("iaq.property.options.property_unit_designation");
      default:
        return null;
    }
  };

  return (
    <div
      style={style}
      className={classnames("tw-relative", {
        "tw-pb-3": preview && isOpen,
        "tw-overflow-hidden": preview && !isOpen,
      })}
    >
      {preview && !disableEdit ? (
        <Text
          role="button"
          tabIndex="0"
          aria-label={editButtonText}
          size="sm"
          className="tw-absolute tw-font-bold tw-text-primary tw-p-3 tw-mr-[-0.75rem] tw-mt-[-0.75rem]"
          style={{ top: "0", right: "0" }}
          onClick={!disableEdit && ToggleFieldRequest}
        >
          {editButtonText}
        </Text>
      ) : null}

      <Suspense fallback={<Fragment />}>
        {!isOpen && preview && (
          <>
            <FieldRequestPreview
              displayName={customLabel() ? customLabel() : request.displayName}
              isSubmitted={request.isSubmitted}
            >
              <FieldRequestForm
                fieldRequestID={fieldRequestID}
                isPreviewMode={preview}
                withInputLabel={withInputLabel}
              />
            </FieldRequestPreview>
          </>
        )}
      </Suspense>

      <Suspense fallback={<Fragment />}>
        {(isOpen || !preview) && (
          <section className="tw-flex tw-flex-col">
            {preview && (
              <div className="tw-flex tw-items-center tw-pr-9 tw-mb-2">
                <h3 className="tw-text-[1.25rem] tw-order-0 tw-mb-0 tw-pr-2 tw-align-middle">
                  {customLabel() ? customLabel() : request.displayName}
                </h3>
              </div>
            )}

            <div
              className={classnames(
                "tw-relative tw-border tw-border-primary tw-pt-8 tw-px-6 tw-pb-6 tw-w-full tw-order-2 tw-rounded-lg",
              )}
            >
              {request.subtitle && (
                <Text size="sm" className="tw-mb-3 tw-font-bold">
                  {request.subtitle}
                </Text>
              )}

              <FieldRequestForm
                fieldRequestID={fieldRequestID}
                withInputLabel={withInputLabel}
              />
            </div>

            {request.description && (
              <FieldRequestExplanation
                preview={preview}
                className={classnames("tw-order-4", {
                  "tw-order-4 tw-pr-9": preview,
                  "tw-mt-5 xl:tw-mt-9 xl:tw-order-4": !preview,
                })}
                title={t("iaq.whyImportantHeading")}
                text={request.description}
              />
            )}
          </section>
        )}
      </Suspense>
    </div>
  );
};

export default EditablePreviewFieldRequest;
